import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RefreshCw, ArrowRight, ExternalLink } from 'lucide-react';

interface TagMapResult {
  id: string;
  tag_id: string;
  slug: string;
  version: string;
  tag_applies: boolean | null;
  confidence: string;
  reason: string;
  quarter: string | null;
  screenshot_url: string;
  annotated_url: string | null;
  analysis_status: string | null;
  created_at: string;
}

interface ComparisonData {
  slug: string;
  q2024: TagMapResult | null;
  q2025: TagMapResult | null;
}

export default function ReportsTest() {
  const [tagId, setTagId] = useState<string | null>(null);
  const [tagVersion, setTagVersion] = useState<number | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [latestVersion, setLatestVersion] = useState<number | null>(null);
  const [results2024Q4, setResults2024Q4] = useState<TagMapResult[]>([]);
  const [results2025Q4, setResults2025Q4] = useState<TagMapResult[]>([]);
  const [comparisonData, setComparisonData] = useState<ComparisonData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    setIsUsingFallback(false);

    try {
      // Step 1: Get all versions of the ai_add_on tag
      const { data: allTags, error: tagsError } = await supabase
        .from('app_tags')
        .select('id, tag_name, version_number')
        .eq('tag_name', 'ai_add_on')
        .order('version_number', { ascending: false });

      if (tagsError) throw new Error(`Tags not found: ${tagsError.message}`);
      if (!allTags || allTags.length === 0) throw new Error('ai_add_on tag not found');

      const latestTag = allTags[0];
      setLatestVersion(latestTag.version_number);

      // Step 2: Try to find data for each version, starting with the latest
      let selectedTag = null;
      let data2024: TagMapResult[] = [];
      let data2025: TagMapResult[] = [];

      for (const tag of allTags) {
        // Try fetching data for this version
        const { data: test2024 } = await supabase
          .from('app_tags_map')
          .select('*')
          .eq('tag_id', tag.id)
          .eq('quarter', '2024Q4')
          .limit(1);

        const { data: test2025 } = await supabase
          .from('app_tags_map')
          .select('*')
          .eq('tag_id', tag.id)
          .eq('quarter', '2025Q4')
          .limit(1);

        // If we found data for either quarter, use this version
        if ((test2024 && test2024.length > 0) || (test2025 && test2025.length > 0)) {
          selectedTag = tag;

          // Fetch all data for this version
          const { data: full2024, error: error2024 } = await supabase
            .from('app_tags_map')
            .select('*')
            .eq('tag_id', tag.id)
            .eq('quarter', '2024Q4')
            .order('slug', { ascending: true });

          const { data: full2025, error: error2025 } = await supabase
            .from('app_tags_map')
            .select('*')
            .eq('tag_id', tag.id)
            .eq('quarter', '2025Q4')
            .order('slug', { ascending: true });

          if (error2024) throw new Error(`2024Q4 query error: ${error2024.message}`);
          if (error2025) throw new Error(`2025Q4 query error: ${error2025.message}`);

          data2024 = full2024 || [];
          data2025 = full2025 || [];
          break;
        }
      }

      if (!selectedTag) {
        throw new Error('No data found for any version of ai_add_on tag in 2024Q4 or 2025Q4');
      }

      setTagId(selectedTag.id);
      setTagVersion(selectedTag.version_number);
      setIsUsingFallback(selectedTag.version_number !== latestTag.version_number);
      setResults2024Q4(data2024);
      setResults2025Q4(data2025);

      // Step 4: Create comparison data
      const slugs = new Set([
        ...(data2024 || []).map(r => r.slug),
        ...(data2025 || []).map(r => r.slug),
      ]);

      const comparison: ComparisonData[] = Array.from(slugs).map(slug => ({
        slug,
        q2024: (data2024 || []).find(r => r.slug === slug) || null,
        q2025: (data2025 || []).find(r => r.slug === slug) || null,
      }))
        // Only show rows where tag applies (is true) in at least one quarter
        .filter(item => item.q2024?.tag_applies === true || item.q2025?.tag_applies === true)
        .sort((a, b) => a.slug.localeCompare(b.slug));

      setComparisonData(comparison);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (applies: boolean | null, confidence: string) => {
    if (applies === null) {
      return <Badge variant="outline">Unknown</Badge>;
    }
    if (applies) {
      return (
        <Badge className="bg-green-100 text-green-800">
          Yes ({confidence})
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-800">
        No ({confidence})
      </Badge>
    );
  };

  const getChangeIndicator = (q2024: TagMapResult | null, q2025: TagMapResult | null) => {
    const applies2024 = q2024?.tag_applies === true;
    const applies2025 = q2025?.tag_applies === true;

    if (!applies2024 && applies2025) {
      return <Badge className="bg-blue-100 text-blue-800">New in 2025</Badge>;
    }
    if (applies2024 && !applies2025) {
      return <Badge className="bg-gray-100 text-gray-800">Removed in 2025</Badge>;
    }
    if (applies2024 && applies2025) {
      if (q2024.confidence !== q2025.confidence) {
        return <Badge className="bg-yellow-100 text-yellow-800">Confidence Changed</Badge>;
      }
      return <Badge variant="outline">No Change</Badge>;
    }
    return null;
  };

  const handleImageClick = (url: string, slug: string, quarter: string) => {
    setSelectedImage({ url, title: `${slug} - ${quarter}` });
    setImageModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
            <p className="text-gray-600">Loading tag comparison data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <p className="text-red-600">Error: {error}</p>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-[1600px]">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Tag Analysis: AI Add-On</h1>
            <p className="text-gray-600 mt-1">
              Comparing tag "ai_add_on" (v{tagVersion || '?'}) between 2024Q4 and 2025Q4
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        {isUsingFallback && latestVersion && tagVersion && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-800">Using Older Tag Version</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  The latest version (v{latestVersion}) has no data for the selected quarters.
                  Showing results for v{tagVersion} instead.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600">Total Analyzed</p>
          <p className="text-2xl font-bold">{comparisonData.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600">2024Q4 Total</p>
          <p className="text-2xl font-bold">
            {comparisonData.filter(c => c.q2024?.tag_applies === true).length}
          </p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600">2025Q4 Total</p>
          <p className="text-2xl font-bold">
            {comparisonData.filter(c => c.q2025?.tag_applies === true).length}
          </p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600">Change %</p>
          <p className="text-2xl font-bold">
            {(() => {
              const total2024 = comparisonData.filter(c => c.q2024?.tag_applies === true).length;
              const total2025 = comparisonData.filter(c => c.q2025?.tag_applies === true).length;
              if (total2024 === 0) return total2025 > 0 ? '+∞' : '0%';
              const change = ((total2025 - total2024) / total2024) * 100;
              return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
            })()}
          </p>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">Company</TableHead>
              <TableHead className="w-32">Change</TableHead>
              <TableHead>2024Q4 Status</TableHead>
              <TableHead className="w-24">2024Q4 Image</TableHead>
              <TableHead className="w-16"></TableHead>
              <TableHead>2025Q4 Status</TableHead>
              <TableHead className="w-24">2025Q4 Image</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comparisonData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No results found for comparison
                </TableCell>
              </TableRow>
            ) : (
              comparisonData.map((item) => (
                <TableRow key={item.slug}>
                  <TableCell className="font-medium">{item.slug}</TableCell>
                  <TableCell>{getChangeIndicator(item.q2024, item.q2025)}</TableCell>
                  <TableCell>
                    {item.q2024 ? (
                      <div className="space-y-1">
                        {getStatusBadge(item.q2024.tag_applies, item.q2024.confidence)}
                        {item.q2024.reason && (
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {item.q2024.reason}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.q2024?.annotated_url ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleImageClick(item.q2024!.annotated_url!, item.slug, '2024Q4')}
                        className="h-8 text-xs"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </TableCell>
                  <TableCell>
                    {item.q2025 ? (
                      <div className="space-y-1">
                        {getStatusBadge(item.q2025.tag_applies, item.q2025.confidence)}
                        {item.q2025.reason && (
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {item.q2025.reason}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.q2025?.annotated_url ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleImageClick(item.q2025!.annotated_url!, item.slug, '2025Q4')}
                        className="h-8 text-xs"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Raw Data Debug Info */}
      <div className="mt-6 space-y-4">
        <details className="bg-gray-50 border rounded-lg p-4">
          <summary className="font-semibold cursor-pointer">2024Q4 Raw Data ({results2024Q4.length} results)</summary>
          <pre className="mt-4 text-xs overflow-auto max-h-96 bg-white p-4 rounded">
            {JSON.stringify(results2024Q4, null, 2)}
          </pre>
        </details>

        <details className="bg-gray-50 border rounded-lg p-4">
          <summary className="font-semibold cursor-pointer">2025Q4 Raw Data ({results2025Q4.length} results)</summary>
          <pre className="mt-4 text-xs overflow-auto max-h-96 bg-white p-4 rounded">
            {JSON.stringify(results2025Q4, null, 2)}
          </pre>
        </details>
      </div>

      {/* Image Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{selectedImage?.title}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-100px)]">
            {selectedImage && (
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
